import { filterSRP } from '../integrations/search.js';
import { hideVdpLink } from '../helpers/hacks.js';
import { trackEvent, trackWidgetUserEvent } from '../events/trackEvent.js';
import { updateShopper } from '../integrations/accelerate.js';
import { mapAMDDataToWallet } from '../utils/mapAMDDataToWallet.js';
import {
	LS_PENCIL_EVENT_KEY,
	MMD_TRACK_EVENT_NAME,
	USER_SHOW_PAYMENTS_KEY,
	WALLET_CONFIG_KEY,
	WALLET_UPDATE_STATE_SOURCE,
	SDP_CLOSE_EVENTS,
	UDS_SCRIPT_NAME,
	AMD_UNUSED_EVENTS,
	TIMING_LABELS,
} from '../global-constants.js';
import { getWalletConfig, getWalletUpdateState, setWalletUpdateState } from '../helpers/index.js';
import { buildSDPInitObject } from '../utils/buildSDPInitObject.js';
import { clearSdpUrlParams } from '../helpers/clearParams.js';
import { getValidatedVins } from '../pricing/payments/wsInvDataApi.js';
import { ddcLog, markPerformance, measurePerformance } from '../utils/logger.js';

const handleWalletUpdates = (data, vehicle, isSRP) => {
	let dataObj = { ...data };
	// If this update came from the wallet modal,
	// track this as an "auto pencil" update for AMD in updateShopper
	if (data?.myWallet?.displayModal && !isSRP && window.CADigitalRetailObject) {
		dataObj = { ...dataObj, autoPencil: true };
	}

	// If this update came from AMD
	// no need to refresh accelerate
	let refreshAccelerate = false;
	const { lastUpdateSource = null } = getWalletUpdateState();

	// If this update did not originate in AMD
	// set update as from wallet
	if (lastUpdateSource !== WALLET_UPDATE_STATE_SOURCE.AMD) {
		refreshAccelerate = true;
		setWalletUpdateState(WALLET_UPDATE_STATE_SOURCE.WALLET);
	} else {
		setWalletUpdateState(null);
	}

	updateShopper(dataObj, vehicle, null, refreshAccelerate);
};

export const activateWalletEventListeners = (API, isSRP, config, vehicles, indexPage, siteId, walletScriptAssetsHost, detailPage) => {
	const {
		enableDealDashboard,
		refreshDsAfterSdpClose,
		showPayments,
		enableEsntialUds,
	} = config;

	const [vehicle] = vehicles;
	let walletUpdatedTracked = false;

	// Listen for search events. Filter the SRP to the selected search when on the SRP,
	// or navigate to the SRP and perform the search if the user is currently on the VDP.
	window.caiEventBus.on('wallet:search', async (data) => {

		trackEvent({
			element: 'Search available cars CTA',
			action: 'clicked',
			result: 'User navigates to filtered SRP'
		});

		window.caiEventBus.emit('wallet:close');

		if (isSRP) {
			filterSRP(data, config.inventoryPriceField);
		} else {
			// populate sitemap only if we're not on SRP
			const siteMap = await API.utils.getSitemap();

			let redirectPage = siteMap.INVENTORY_LISTING_DEFAULT_AUTO_NEW;
			if (vehicle?.inventoryType === 'used') {
				redirectPage = siteMap.INVENTORY_LISTING_DEFAULT_AUTO_USED;
			}
			if(indexPage){
				let budgetRange = '';
				if(data.budget > 0){
					const budgetUpper = parseInt(data.budget, 10);
					budgetRange = `1-${budgetUpper}`;
				}
				const defaultField = 'internetPrice';
				window.location.href = `${redirectPage}?walletFilter=true&${defaultField}=${budgetRange}`;
			}
			else{
				window.location.href = `${redirectPage}?walletFilter=true`;
			}
		}
	});

	window.caiEventBus.on('wallet:open', (data = {}) => {
		const { inventory } = data;
		const { id } = inventory || {};
		const vehicleToUse = vehicles.find(v => v.uuid === id) || undefined;


		// TODO Remove this hack once there is an option to hide the VDP link when opening the modal.
		if (!isSRP) {
			hideVdpLink();
		}

		// Hide the Show Payments tooltip any time the user interacts with My Wallet.
		window.sessionStorage.setItem('userHideShowPaymentsTooltip', true);

		trackEvent({
			element: 'My Wallet modal',
			action: 'clicked',
			result: 'My Wallet modal presented',
			vehicle: vehicleToUse
		});
	});

	// listen for wallet updates
	window.caiEventBus.on('wallet:updatePaymentsInfo:completed', (data) => {
		ddcLog('wallet:updatePaymentsInfo:completed', data);
		if (!walletUpdatedTracked) {
			trackEvent({
				element: 'My Wallet field',
				action: 'changed',
				result: 'My Wallet field updated'
			});
			walletUpdatedTracked = true;
		}
		handleWalletUpdates(data?.updatedPaymentsInfo, vehicle, isSRP);

		if (data?.updatedPayload?.paymentOption && data.updatedPayload.interactions?.hasUpdatedPaymentOption
			&& data?.updatedPaymentsInfo) {
			// track payment update
			trackWidgetUserEvent({
				element: 'MyWallet Modal: Payment Settings Update',
				action: `${data.updatedPaymentsInfo?.paymentOption} selected`,
				data,
				eventName: null
			});
		}
	});

	// If payments are enabled for this site, request payment data and insert into the page.
	// Listen for Wallet config updates and filter the SRP accordingly when an update occurs.
	let hasInitializedUDSEsntial = false;
	window.caiEventBus.on('wallet:ensurePaymentsInfo:completed', async ({ paymentInfo }) => {
		ddcLog('wallet:ensurePaymentsInfo:completed -> paymentInfo', paymentInfo);

		// This is a temporary fix to ensure the UDS heading font weight is the same as the DDC heading font weight
		// until the UDS team can provide a fix for this.
		const headingElement = document.querySelector('h3');
		const bodyElement = document.querySelector('body');
		const ddcHeadingFontWeight =  headingElement ?
			window.getComputedStyle(headingElement).fontWeight :
			window.getComputedStyle(bodyElement).fontWeight;

		window.caiEventBus.on('uds:calcConfig:completed', () => {
			const applyUdsHeadingStyle = window.setInterval(() => {
				const udsHeadingElement = document
					.querySelector('unified-deal-starter')
					?.shadowRoot
					?.querySelector('div')
					?.shadowRoot
					?.querySelector('[data-cmp="heading"]');

				// Check if the UDS heading element exists
				if (udsHeadingElement) {
					// Apply DDC heading font weight to the UDS heading element
					udsHeadingElement.style.setProperty('font-weight', ddcHeadingFontWeight, 'important');
					// Clear the interval once the element is found and updated
					clearInterval(applyUdsHeadingStyle);
				}
			}, 100);

			// Fallback to clear the interval after 5 seconds if the element is not found
			setTimeout(() => {
				clearInterval(applyUdsHeadingStyle);
			}, 5000);
		});

		// If this is the first load of page with wallet
		// local storage for wallet config will not exist
		// store the initial object in local storage as fallback
		if(!localStorage.getItem(WALLET_CONFIG_KEY)) {
			localStorage.setItem(WALLET_CONFIG_KEY, JSON.stringify(paymentInfo));
		}

		const urlParams = API.utils.getUrlParams();

		if (isSRP && urlParams.walletFilter) {
			filterSRP(paymentInfo, config.inventoryPriceField);
		}

		setWalletUpdateState(null);

		// TODO: temporary fix while syndication fix has opened wallet interaction
		const userShowPayments = JSON.parse(localStorage.getItem(USER_SHOW_PAYMENTS_KEY));
		const shouldShowPayments = userShowPayments?.show;

		// only update the shopper on wallet:ensurePaymentsInfo:completed if the user has interacted with wallet
		if (paymentInfo?.interactions?.hasOpenedWallet || shouldShowPayments) {
			updateShopper(paymentInfo, vehicle, null, true);
		}

		if (showPayments) {
			await import(
				/* webpackChunkName: 'pricing' */
				'../pricing.js'
				);
		}

		if (detailPage && enableEsntialUds && !hasInitializedUDSEsntial) {
			hasInitializedUDSEsntial = true;
			const {
				START_ESNTIAL_UDS_INT_LOAD,
				START_UDS_SCRIPT_LOAD,
				END_UDS_SCRIPT_LOAD,
				MEASURE_UDS_SCRIPT_LOAD,
			} = TIMING_LABELS;
			markPerformance(START_ESNTIAL_UDS_INT_LOAD);
			markPerformance(START_UDS_SCRIPT_LOAD);
			await API.loadJS(`${walletScriptAssetsHost}/${UDS_SCRIPT_NAME}.js`);
			markPerformance(END_UDS_SCRIPT_LOAD);
			measurePerformance(MEASURE_UDS_SCRIPT_LOAD, START_UDS_SCRIPT_LOAD, END_UDS_SCRIPT_LOAD);

			await import(
				/* webpackChunkName: 'esntial-uds' */
				'../integrations/esntial-uds.js'
				);
		}
	});

	// this is custom event to update wallet data when user interacts with deal starter
	// eventObj includes eventName, eventAction and eventData from onTrack hook
	window.caiEventBus.on('wallet:updateFromAMD', (eventObj) => {
		ddcLog('wallet:updateFromAMD', eventObj);
		const { eventName } = eventObj;

		// If this is a close event for SDP
		// clear out the url params that prevent
		// SDP from opening on correct route
		// when user interacts with external SDP CTAs
		if (SDP_CLOSE_EVENTS.includes(eventName)) {
			clearSdpUrlParams();
		}

		// only hand off this update to wallet if the event is relevant
		if (!AMD_UNUSED_EVENTS.includes(eventName)) {
			ddcLog('updateFromAMD -> ATTEMPTING TO UPDATE WALLET FROM AMD', eventName);
			const { lastUpdateSource = null } = getWalletUpdateState();

			// If this update originated in Wallet
			// no need to update wallet with info
			// it already has
			if (lastUpdateSource && lastUpdateSource === WALLET_UPDATE_STATE_SOURCE.WALLET) {
				ddcLog('updateFromAMD -> LAST UPDATE WAS FROM WALLET -> BYPASSING UPDATE', eventName);
				setWalletUpdateState(null);
				return;
			}

			// If this update didn't originate in Wallet
			// Mark this update as coming from AMD
			// so we don't unecessarily refresh DS
			setWalletUpdateState(WALLET_UPDATE_STATE_SOURCE.AMD);

			const newWalletData = mapAMDDataToWallet(eventObj);
			ddcLog('updateFromAMD -> UPDATE WAS FROM AMD AND IS VALID -> EMITTING NEW WALLET UPDATE NOW', eventName);
			// set wallet data
			window.caiEventBus.emit('wallet:updatePaymentsInfo:request', newWalletData);
		}

		// onHidingFunction CADigitalRetailObject is not working, This should handle DS SDP entry points
		// TODO: this may not be required when we sync SDP, DS and Wallet data.
		if (refreshDsAfterSdpClose && eventName === 'DR_SDP_LEAVE_BUTTON_CLICKED' && typeof window.refreshAccelerateWidget === 'function') {
			window.refreshAccelerateWidget();
		}

		// send 'auto pencil' event data in local storage to GA
		const lsEventData = localStorage.getItem(LS_PENCIL_EVENT_KEY);
		if (
			lsEventData &&
			Object.keys(lsEventData)?.length > 0 &&
			eventName === MMD_TRACK_EVENT_NAME
		) {
			trackWidgetUserEvent({
				element: 'Pencil update',
				action: 'auto updated',
				data: lsEventData,
				eventName: null,
				vehicle
			});
			localStorage.removeItem(LS_PENCIL_EVENT_KEY);
		}
	});

	// this is event to validate deals by vins when enableDealDashboard flag is turn on

	if (enableDealDashboard) {

		window.caiEventBus.on('wallet:validateDealsByVin:request', async(vins) => {

			if(Array.isArray(vins) && vins.length > 0) {

				// call ws-inv-data endpoint
				const wsInvDataResponse = await getValidatedVins(vins, siteId);

				window.caiEventBus.emit('wallet:validateDealsByVin:completed', wsInvDataResponse);
			}
		});

		window.caiEventBus.on('wallet:emailDealer:request', ({_, vehicle: vehicleObj, vdpUrl}) => {

			const regex = /([a-f0-9]){32}/;
			const match = vdpUrl.match(regex);
			const uuid = match ? match[0] : '';

			const { condition } = vehicleObj;

			const pageAlias = window.DDC?.siteSettings?.pageAlias || '';
			const emailLinkId = `sdpEmailLink_${uuid}`;
			let $emailLink = $(`#${emailLinkId}`);
			// if link for this vehicle lead form isn't already on the page, we'll need to create it
			if (!$emailLink.length) {
				// create link markup
				// eslint-disable-next-line
				const linkMarkup = `<a id="${emailLinkId}" class="hide dialog" href="#?parentPageAlias=${pageAlias}" rel="nofollow"  data-width="375" data-href="/lead-form.htm?itemId=${uuid}&vehicleId=${uuid}&category=${condition}&parentPageAlias=${pageAlias}" data-title="Request More Info" data-link-label="ASK" data-location="vehicle-ask-button">`;
				// create new element using jQuery
				$emailLink = $(linkMarkup);
				// append that element to body
				$('body').append($emailLink);
				// trigger refresh on dialog jQuery module
				$(document).trigger('modulesRefresh', { modules: { 'dialog' : window.DDC?.modules?.dialog } });
			}

			// trigger dialog open
			$emailLink.click();
		});
	}

	window.caiEventBus.on('wallet:openSdp:request', async({ route, vdpUrl, dealXgId }) => {
		if (route && vdpUrl && dealXgId) {
			window.location.href = `${vdpUrl}?autoOpen=true&dsLoad=true&route=${route}&dealXgId=${dealXgId}#mmd-widget`;
		} else if (route && window.accelerate) {
			const walletData = await getWalletConfig();
			const initObj = buildSDPInitObject(walletData, route, refreshDsAfterSdpClose);
			window.accelerate?.openShopperPlatform(initObj);
		}
	});
}
